import { Language, TranslationMode } from '@lingvo-news/contracts';

export const LINGVO_NEWS_PREFERENCES_STORAGE_KEY = 'lingvo-news-preferences';

export interface StoredPreferences {
	readonly nativeLanguage: Language;
	readonly learnedLanguage: Language;
	readonly mode: TranslationMode;
}

export interface RoutePreferences {
	readonly nativeLanguage: Language;
	readonly learnedLanguage: Language;
	readonly mode: TranslationMode;
}

function hasLocalStorage(): boolean {
	return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object';
}

function isLanguage(value: unknown): value is Language {
	return typeof value === 'string' && Object.values(Language).includes(value as Language);
}

function normalizeTranslationMode(value: unknown): TranslationMode | null {
	if (typeof value !== 'string') return null;
	const normalized = value.toLowerCase();
	if (normalized === TranslationMode.Partial || normalized === TranslationMode.Full) {
		return normalized as TranslationMode;
	}
	return null;
}

export function readPreferencesRecord(): Record<string, unknown> | null {
	try {
		if (!hasLocalStorage()) {
			return null;
		}
		const stored = window.localStorage.getItem(LINGVO_NEWS_PREFERENCES_STORAGE_KEY);
		if (!stored) return null;

		const parsed: unknown = JSON.parse(stored);
		if (!isRecord(parsed)) return null;

		return parsed;
	} catch {
		return null;
	}
}

export function readNativeLanguageStringPreference(): string | null {
	const record = readPreferencesRecord();
	if (!record) return null;
	const value = record.nativeLanguage;
	return typeof value === 'string' ? value : null;
}

export function readNativeLanguagePreference(): Language | null {
	const record = readPreferencesRecord();
	if (!record) return null;
	const value = record.nativeLanguage;
	return isLanguage(value) ? value : null;
}

export function readStoredPreferences(): StoredPreferences | null {
	const record = readPreferencesRecord();
	if (!record) return null;

	const nativeLanguage = record.nativeLanguage;
	const learnedLanguage = record.learnedLanguage;
	const mode = normalizeTranslationMode(record.mode);

	if (!isLanguage(nativeLanguage)) return null;
	if (!isLanguage(learnedLanguage)) return null;
	if (!mode) return null;

	return {
		nativeLanguage,
		learnedLanguage,
		mode,
	};
}

export function getRouteParamsForStoredPreferences(preferences: StoredPreferences): RoutePreferences {
	return {
		nativeLanguage: preferences.nativeLanguage,
		learnedLanguage: preferences.learnedLanguage,
		mode: preferences.mode,
	};
}


